import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Icons from 'lucide-react';
import { ArrowUpRight, CheckCircle2, ChevronRight } from 'lucide-react';
import { services } from '../mock/mock';
import { getServiceDetail } from '../mock/serviceDetails';
import CtaBanner from '../components/CtaBanner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const SITE_URL = 'https://residentialsupport.co.uk';

const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-xs text-[var(--rs-ink)]/55" data-testid="service-breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {item.to ? (
            <Link to={item.to} className="hover:text-[var(--rs-coral)]">
              {item.label}
            </Link>
          ) : (
            <span className="text-[var(--rs-ink)]/80 font-medium" aria-current="page">
              {item.label}
            </span>
          )}
          {i < items.length - 1 && <ChevronRight className="w-3 h-3 text-[var(--rs-ink)]/40" />}
        </li>
      ))}
    </ol>
  </nav>
);

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);
  const detail = getServiceDetail(id);

  if (!service || !detail) return <Navigate to="/services" replace />;

  const Icon = Icons[service.icon] || Icons.Sparkles;
  const url = `${SITE_URL}/services/${service.id}`;

  // Other services for the "explore more" strip
  const others = services.filter((s) => s.id !== service.id).slice(0, 3);

  // JSON-LD
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: SITE_URL + '/services' },
      { '@type': 'ListItem', position: 3, name: service.title, item: url },
    ],
  };

  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    serviceType: service.title,
    alternateName: detail.alsoCalled,
    description: detail.seoDescription,
    image: service.image,
    url,
    provider: {
      '@type': 'Organization',
      name: 'Residential Support Ltd',
      url: SITE_URL,
    },
    areaServed: { '@type': 'Country', name: 'United Kingdom' },
    offers: service.from
      ? {
          '@type': 'Offer',
          price: String(service.from),
          priceCurrency: 'GBP',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: service.from,
            priceCurrency: 'GBP',
            valueAddedTaxIncluded: false,
          },
          availability: 'https://schema.org/InStock',
        }
      : undefined,
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: detail.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div>
      <Helmet>
        <title>{detail.seoTitle}</title>
        <meta name="description" content={detail.seoDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Residential Support" />
        <meta property="og:title" content={detail.seoTitle} />
        <meta property="og:description" content={detail.seoDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={service.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={detail.seoTitle} />
        <meta name="twitter:description" content={detail.seoDescription} />
        <meta name="twitter:image" content={service.image} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-[var(--rs-cream)] border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-12 lg:pb-16">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Services', to: '/services' },
              { label: service.title },
            ]}
          />
          <div className="grid lg:grid-cols-12 gap-10 mt-6 items-center">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-[var(--rs-green)]/10 text-[var(--rs-green)] grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)]">
                  Service
                </div>
              </div>
              <h1
                className="h-display text-4xl md:text-5xl lg:text-6xl text-[var(--rs-ink)] leading-[1.05]"
                data-testid="service-title"
              >
                {service.title}
              </h1>
              <p className="mt-5 text-lg text-[var(--rs-ink)]/75 max-w-xl">{detail.tagline}</p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid="service-cta-contact"
                >
                  Talk to us
                </Link>
                {service.from ? (
                  <span className="text-sm text-[var(--rs-ink)]/65">
                    From{' '}
                    <span className="font-semibold text-[var(--rs-ink)]">£{service.from}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <div className="lg:col-span-6">
              <img
                src={service.image}
                alt={`${service.title} — ${detail.tagline}`}
                className="w-full h-[360px] lg:h-[440px] object-cover rounded-[24px] shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Long description */}
      <section className="sect bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <p className="text-[var(--rs-ink)]/80 text-lg leading-relaxed">
            {detail.longDescription}
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="sect bg-[var(--rs-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">
                What&apos;s included
              </div>
              <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)] leading-snug">
                Everything you need in one booking.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4" data-testid="service-features">
                {detail.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[var(--rs-ink)]/85">
                    <CheckCircle2 className="w-5 h-5 text-[var(--rs-coral)] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="sect bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">
            How it works
          </div>
          <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)] mb-10">
            From order to delivered report.
          </h2>
          <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-5" data-testid="service-process">
            {detail.process.map((step, i) => (
              <li
                key={i}
                className="rs-card p-6 lift"
                data-testid={`service-process-step-${i + 1}`}
              >
                <div className="text-xs font-bold text-[var(--rs-coral)] tracking-widest">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="h-display text-lg text-[var(--rs-ink)] mt-2">{step.title}</h3>
                <p className="text-sm text-[var(--rs-ink)]/70 mt-2">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQs */}
      <section className="sect bg-[var(--rs-cream)]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--rs-green)] mb-3">
            Frequently asked
          </div>
          <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)] mb-8">
            Common questions about {service.title.toLowerCase()}.
          </h2>
          <Accordion type="single" collapsible className="bg-white rounded-[20px] px-6 border border-[#E7E0D2]" data-testid="service-faqs">
            {detail.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-[#EDE7D6] last:border-b-0"
                data-testid={`service-faq-${i}`}
              >
                <AccordionTrigger className="text-base text-[var(--rs-ink)] py-5 font-medium">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[var(--rs-ink)]/75 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Other services */}
      <section className="sect bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between gap-4 mb-8">
            <h2 className="h-display text-3xl md:text-4xl text-[var(--rs-ink)]">
              Explore more services
            </h2>
            <Link
              to="/services"
              className="text-sm text-[var(--rs-coral)] inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              All services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="service-related">
            {others.map((s) => {
              const OIcon = Icons[s.icon] || Icons.Sparkles;
              return (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  className="rs-card overflow-hidden lift group"
                  data-testid={`service-related-${s.id}`}
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2.5">
                      <OIcon className="w-4 h-4 text-[var(--rs-green)]" />
                      <h3 className="h-display text-lg text-[var(--rs-ink)]">{s.title}</h3>
                    </div>
                    <p className="text-sm text-[var(--rs-ink)]/70 mt-2">{s.short}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default ServiceDetail;
